// ⌘K actions navigate to an app and then ask it to do something (open the
// composer, the new-contact editor, the upload picker…). The target route is a
// lazy chunk that is usually still loading when the palette fires, so a plain
// window event was always lost. A request is parked here until the route
// registers its handler, and expires so a stale one can't fire much later.
import { useEffect, useRef } from "react";

export type AppCommand =
  | "compose"
  | "mark-all-read"
  | "new-event"
  | "new-contact"
  | "upload-file"
  | "start-meeting";

const PENDING_TTL_MS = 10_000;
const pending = new Map<AppCommand, number>(); // command -> requested at
const handlers = new Map<AppCommand, () => void>();

export function runCommand(name: AppCommand) {
  const handler = handlers.get(name);
  if (handler) handler();
  else pending.set(name, Date.now());
}

/** Handle `name` while mounted. `ready` holds a parked request until the
 *  surface can act on it (e.g. mailboxes loaded). */
export function useCommand(name: AppCommand, handler: () => void, ready = true) {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    if (!ready) return;
    const run = () => latest.current();
    handlers.set(name, run);
    const at = pending.get(name);
    if (at !== undefined) {
      pending.delete(name);
      if (Date.now() - at < PENDING_TTL_MS) run();
    }
    return () => {
      if (handlers.get(name) === run) handlers.delete(name);
    };
  }, [name, ready]);
}
